import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { actionsApi } from '../api/actions'
import type { ActionSharedState, ActionStatus, AdPlatform, Priority } from '../lib/types'
import { ACTION_STATUS_LABEL, ACTION_STATUS_OPTIONS, PLATFORM_LABEL, formatDateBR } from '../lib/format'
import { containsDocumentLink, DOC_LINK_IN_NOTE_MESSAGE } from '../lib/contextLinks'
import { CategoryBadge, PriorityBadge } from './PriorityBadge'
import { ActionStateBadge } from './ActionStateView'
import { ReasonChips, reasonFitsStatus, reasonMissing } from './ReasonChips'
import { Spinner } from './Spinner'

interface ActionLike extends ActionSharedState {
  id: string
  description: string
  rationale: string | null
  priority: Priority
  category: string
  platform?: AdPlatform | null
  campaign?: string | null
}

export function ActionCard({
  action,
  kind,
  reportId,
}: {
  action: ActionLike
  kind: 'optimization' | 'derived'
  reportId: string
}) {
  const qc = useQueryClient()
  // Estado ÚNICO da ação: o formulário edita o registro do time, não uma cópia
  // pessoal. Salvar sobrescreve status e comentário para todo mundo.
  const [status, setStatus] = useState<ActionStatus>(action.state.status)
  const [note, setNote] = useState(action.state.note ?? '')
  const [reason, setReason] = useState<string | null>(action.state.reason ?? null)
  const [reviewAt, setReviewAt] = useState(action.state.reviewAt ?? '')
  const [noteError, setNoteError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        status,
        note: note.trim() ? note : null,
        reason: reasonFitsStatus(status, reason) ? reason : null,
        reviewAt: status === 'review' ? reviewAt : null,
      }
      return kind === 'optimization'
        ? actionsApi.logOptimization(action.id, body)
        : actionsApi.logDerived(action.id, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  const handleSave = () => {
    if (containsDocumentLink(note)) {
      setNoteError(DOC_LINK_IN_NOTE_MESSAGE)
      return
    }
    setNoteError(null)
    mutation.mutate()
  }

  const changeStatus = (s: ActionStatus) => {
    setStatus(s)
    if (!reasonFitsStatus(s, reason)) setReason(null)
  }

  const dirty =
    status !== action.state.status ||
    note !== (action.state.note ?? '') ||
    (reason ?? null) !== (action.state.reason ?? null) ||
    (status === 'review' && reviewAt !== (action.state.reviewAt ?? ''))
  const canSave = dirty && (status !== 'review' || !!reviewAt) && !reasonMissing(status, reason)

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PriorityBadge priority={action.priority} />
        <CategoryBadge category={action.category} />
        {(action.platform || action.campaign) && (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500">
            {[action.platform ? PLATFORM_LABEL[action.platform] : null, action.campaign].filter(Boolean).join(' · ')}
          </span>
        )}
        <ActionStateBadge state={action.state} />
      </div>
      <p className="font-medium text-slate-900">{action.description}</p>
      {action.rationale && (
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-medium">Por que: </span>
          {action.rationale}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {ACTION_STATUS_OPTIONS.map((opt) => (
          <label key={opt} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
            <input
              type="radio"
              name={`status-${action.id}`}
              checked={status === opt}
              onChange={() => changeStatus(opt)}
              className="text-brand-600 focus:ring-brand-500"
            />
            {ACTION_STATUS_LABEL[opt]}
          </label>
        ))}
      </div>

      <ReasonChips status={status} value={reason} onChange={setReason} />

      {status === 'review' && (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          Rever em:
          <input
            type="date"
            value={reviewAt}
            onChange={(e) => setReviewAt(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </label>
      )}

      <div className="mt-2 space-y-2">
        {action.state.by && (
          <p className="text-xs text-slate-400">
            Último registro por {action.state.by}
            {action.state.at ? ` em ${formatDateBR(action.state.at)}` : ''}
          </p>
        )}
        <textarea
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            if (noteError) setNoteError(null)
          }}
          rows={3}
          placeholder="Comentário da ação (visível para todos)"
          className="w-full resize-y rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
        {noteError && <p className="text-xs text-red-600">{noteError}</p>}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={mutation.isPending || !canSave}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending && <Spinner className="h-4 w-4 text-white" />}
            Salvar
          </button>
        </div>
      </div>

      {mutation.isError && (
        <p className="mt-1 text-xs text-red-600">Erro ao salvar. Tente novamente.</p>
      )}
      {mutation.isSuccess && !dirty && !mutation.isPending && (
        <p className="mt-1 text-xs text-green-600">Salvo ✓</p>
      )}
    </div>
  )
}
